import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import MetricsCard from './MetricsCard';

const EmailMarketingPanel = () => {
  const [campaigns, setCampaigns] = useState([
    {
      id: 1,
      name: 'Summer Wedding Promotions',
      subject: 'Get 20% Off Summer Wedding Packages',
      status: 'sent',
      sentTo: 2450,
      openRate: 24.5,
      clickRate: 8.2,
      sentAt: '2025-07-10T10:00:00Z',
      type: 'promotional'
    },
    {
      id: 2,
      name: 'Weekly Newsletter - July',
      subject: 'Wedding Tips & Latest Vendor Updates',
      status: 'scheduled',
      sentTo: 0,
      openRate: 0,
      clickRate: 0,
      scheduledFor: '2025-07-15T09:00:00Z',
      type: 'newsletter'
    },
    {
      id: 3,
      name: 'Profile Completion Reminder',
      subject: 'Complete Your Profile for Better Matches',
      status: 'draft',
      sentTo: 0,
      openRate: 0,
      clickRate: 0,
      type: 'automated'
    }
  ]);

  const [templates, setTemplates] = useState([
    {
      id: 1,
      name: 'Welcome Email',
      type: 'welcome',
      subject: 'Welcome to ShaadiNest!',
      previewText: 'Thank you for joining our wedding planning community...'
    },
    {
      id: 2,
      name: 'Vendor Spotlight',
      type: 'promotional',
      subject: 'Featured Vendor of the Month',
      previewText: 'Discover amazing vendors in your area...'
    },
    {
      id: 3,
      name: 'Booking Confirmation',
      type: 'transactional',
      subject: 'Your Booking is Confirmed',
      previewText: 'We\'re excited to confirm your vendor booking...'
    }
  ]);

  const [subscribers, setSubscribers] = useState({
    total: 12450,
    active: 11890,
    newThisMonth: 245,
    unsubscribed: 560
  });

  const [newCampaign, setNewCampaign] = useState({
    name: '',
    subject: '',
    template: '',
    audience: 'all-subscribers',
    scheduleType: 'now'
  });

  const [activeTab, setActiveTab] = useState('campaigns');

  const campaignStats = {
    totalCampaigns: campaigns.length,
    sent: campaigns.filter(c => c.status === 'sent').length,
    scheduled: campaigns.filter(c => c.status === 'scheduled').length,
    avgOpenRate: campaigns.filter(c => c.status === 'sent').reduce((sum, c) => sum + c.openRate, 0) / campaigns.filter(c => c.status === 'sent').length || 0
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent': return 'text-green-600 bg-green-100';
      case 'scheduled': return 'text-blue-600 bg-blue-100';
      case 'draft': return 'text-gray-600 bg-gray-100';
      case 'sending': return 'text-orange-600 bg-orange-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'promotional': return 'text-purple-600 bg-purple-100';
      case 'newsletter': return 'text-blue-600 bg-blue-100';
      case 'automated': return 'text-green-600 bg-green-100';
      case 'transactional': return 'text-orange-600 bg-orange-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-pink-600 to-pink-700 rounded-xl p-6 text-white">
        <h2 className="font-heading font-bold text-2xl mb-2">
          Email Marketing Integration
        </h2>
        <p className="text-pink-100">
          Manage email campaigns, templates, and subscriber lists
        </p>
        <div className="mt-4 p-3 bg-pink-500/30 rounded-lg">
          <div className="flex items-center space-x-2">
            <Icon name="Info" size={16} className="text-pink-100" />
            <span className="text-sm text-pink-100">
              Integration placeholder - Connect with your preferred email service provider
            </span>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <MetricsCard
          title="Total Subscribers"
          value={subscribers.total.toLocaleString()}
          change={`+${subscribers.newThisMonth} this month`}
          icon="Users"
          color="blue"
        />
        <MetricsCard
          title="Active Campaigns"
          value={campaignStats.totalCampaigns}
          icon="Mail"
          color="green"
        />
        <MetricsCard
          title="Avg Open Rate"
          value={`${campaignStats.avgOpenRate.toFixed(1)}%`}
          icon="Eye"
          color="purple"
        />
        <MetricsCard
          title="Scheduled"
          value={campaignStats.scheduled}
          icon="Clock"
          color="orange"
        />
      </div>

      {/* Integration Setup */}
      <div className="bg-white rounded-xl shadow-soft p-6">
        <h3 className="font-heading font-semibold text-xl text-text-primary mb-4">
          Email Service Provider Integration
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="border border-gray-200 rounded-lg p-4 text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-3">
              <Icon name="Mail" size={24} className="text-blue-600" />
            </div>
            <h4 className="font-medium text-gray-900 mb-2">Mailchimp</h4>
            <p className="text-sm text-gray-500 mb-3">Popular email marketing platform</p>
            <Button variant="outline" size="sm">Connect</Button>
          </div>
          
          <div className="border border-gray-200 rounded-lg p-4 text-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-3">
              <Icon name="Send" size={24} className="text-green-600" />
            </div>
            <h4 className="font-medium text-gray-900 mb-2">SendGrid</h4>
            <p className="text-sm text-gray-500 mb-3">Reliable email delivery service</p>
            <Button variant="outline" size="sm">Connect</Button>
          </div>
          
          <div className="border border-gray-200 rounded-lg p-4 text-center">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-3">
              <Icon name="Zap" size={24} className="text-purple-600" />
            </div>
            <h4 className="font-medium text-gray-900 mb-2">Constant Contact</h4>
            <p className="text-sm text-gray-500 mb-3">Easy-to-use email marketing</p>
            <Button variant="outline" size="sm">Connect</Button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-soft">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('campaigns')}
            className={`px-6 py-4 font-medium ${
              activeTab === 'campaigns' ?'text-primary border-b-2 border-primary' :'text-gray-500 hover:text-gray-700'
            }`}
          >
            Campaigns
          </button>
          <button
            onClick={() => setActiveTab('templates')}
            className={`px-6 py-4 font-medium ${
              activeTab === 'templates' ?'text-primary border-b-2 border-primary' :'text-gray-500 hover:text-gray-700'
            }`}
          >
            Templates
          </button>
          <button
            onClick={() => setActiveTab('subscribers')}
            className={`px-6 py-4 font-medium ${
              activeTab === 'subscribers' ?'text-primary border-b-2 border-primary' :'text-gray-500 hover:text-gray-700'
            }`}
          >
            Subscribers
          </button>
        </div>

        <div className="p-6">
          {activeTab === 'campaigns' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-heading font-semibold text-xl text-text-primary">
                  Email Campaigns
                </h3>
                <Button variant="primary">
                  <Icon name="Plus" size={16} className="mr-2" />
                  New Campaign
                </Button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Campaign
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Recipients
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Open Rate
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {campaigns.map((campaign) => (
                      <tr key={campaign.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div>
                            <div className="font-medium text-gray-900">{campaign.name}</div>
                            <div className="text-sm text-gray-500">{campaign.subject}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(campaign.type)}`}>
                            {campaign.type}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(campaign.status)}`}>
                            {campaign.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {campaign.sentTo > 0 ? campaign.sentTo.toLocaleString() : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {campaign.openRate > 0 ? `${campaign.openRate}%` : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                          <Button variant="ghost" size="sm">
                            <Icon name="Eye" size={16} />
                          </Button>
                          <Button variant="ghost" size="sm">
                            <Icon name="Edit" size={16} />
                          </Button>
                          <Button variant="ghost" size="sm">
                            <Icon name="BarChart3" size={16} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'templates' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-heading font-semibold text-xl text-text-primary">
                  Email Templates
                </h3>
                <Button variant="primary">
                  <Icon name="Plus" size={16} className="mr-2" />
                  New Template
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {templates.map((template) => (
                  <div key={template.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900">{template.name}</h4>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(template.type)}`}>
                        {template.type}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{template.subject}</p>
                    <p className="text-sm text-gray-500 mb-4">{template.previewText}</p>
                    <div className="flex justify-between">
                      <Button variant="outline" size="sm">
                        <Icon name="Eye" size={14} className="mr-1" />
                        Preview
                      </Button>
                      <Button variant="ghost" size="sm">
                        <Icon name="Edit" size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'subscribers' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-heading font-semibold text-xl text-text-primary">
                  Subscriber Management
                </h3>
                <Button variant="primary">
                  <Icon name="Upload" size={16} className="mr-2" />
                  Import Subscribers
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-blue-600">Total Subscribers</p>
                      <p className="text-2xl font-bold text-blue-900">{subscribers.total.toLocaleString()}</p>
                    </div>
                    <Icon name="Users" size={24} className="text-blue-600" />
                  </div>
                </div>
                
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-green-600">Active</p>
                      <p className="text-2xl font-bold text-green-900">{subscribers.active.toLocaleString()}</p>
                    </div>
                    <Icon name="UserCheck" size={24} className="text-green-600" />
                  </div>
                </div>
                
                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-purple-600">New This Month</p>
                      <p className="text-2xl font-bold text-purple-900">{subscribers.newThisMonth}</p>
                    </div>
                    <Icon name="UserPlus" size={24} className="text-purple-600" />
                  </div>
                </div>
                
                <div className="bg-red-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-red-600">Unsubscribed</p>
                      <p className="text-2xl font-bold text-red-900">{subscribers.unsubscribed}</p>
                    </div>
                    <Icon name="UserMinus" size={24} className="text-red-600" />
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-6 text-center">
                <Icon name="Database" size={48} className="mx-auto text-gray-400 mb-4" />
                <h4 className="font-medium text-gray-900 mb-2">Subscriber List Management</h4>
                <p className="text-gray-600 mb-4">
                  Connect your email service provider to manage subscriber lists, segments, and preferences.
                </p>
                <Button variant="outline">
                  Configure Integration
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailMarketingPanel;